import formatDefinitions from './countryFormats';


// country codes
// https://chromium-i18n.appspot.com/ssl-address/data

// example country format us
// https://chromium-i18n.appspot.com/ssl-address/data/US

export const Address = {
    postalCountry: '',
    administrativeArea: '',
    locality: '',
    dependentLocality: '',
    postalCode: '',
    sortingCode: '',
    organization: '',
    name: '',
    addressLines: []
};

const hasKey = (object, key) => {
    return key in object;
};

const getFormat = (countryCode) => {
    countryCode = countryCode.toUpperCase();
    return hasKey(formatDefinitions, countryCode) ? formatDefinitions[countryCode].fmt : formatDefinitions.ZZ.fmt;
};

const getFormatParts = (format) => {
    const parts = [];
    let literal = '';
    let escaped = false;

    for (const char of format) {
        if (escaped) {
            escaped = false;
            parts.push(`%${char}`);
        } else if (char === '%') {
            if (literal.length > 0) {
                parts.push(literal);
                literal = '';
            }

            escaped = true;
        } else {
            literal += char;
        }
    }

    if (literal.length > 0) {
        parts.push(literal);
    }

    return parts;
};

const fields = {
    '%N': 'name',
    '%O': 'organization',
    '%A': 'addressLines',
    '%D': 'dependentLocality',
    '%C': 'locality',
    '%S': 'administrativeArea',
    '%Z': 'postalCode',
    '%X': 'sortingCode',
};

const getField = (token) => {
    if (hasKey(fields, token)) {
        return fields[token];
    }
    return null;
};

const addressHasField = (address, field) => {
    if (!field) {
        return false;
    }

    if (field === 'addressLines') {
        return address.addressLines && address.addressLines.length > 0;
    }

    return hasKey(address, field) && address[field];
};

const pruneFormat = (formatParts, address) => {
    const prunedFormat = [];

    for (const [i, part] of formatParts.entries()) {
        if (part === '%n') {
            prunedFormat.push(part);
            continue;
        }

        const field = getField(part);

        if (field) {
            if (addressHasField(address, field)) {
                prunedFormat.push(part);
            }

            continue;
        }

        if ((
            i === formatParts.length - i
            || formatParts[i + 1] === '%n'
            || addressHasField(address, getField(formatParts[i + 1]))
        ) && (
            i === 0
            || formatParts[i - 1] === '%n'
            || (prunedFormat.length > 0 && getField(prunedFormat[prunedFormat.length - 1]) !== null)
        )) {
            prunedFormat.push(part);
        }
    }

    return prunedFormat;
};

const pruneAddress = (address) => {
    return {
        ...address,
        addressLines: address.addressLines ? address.addressLines.filter(Boolean) : undefined,
    };
};

const getCountryName = (countryCode) => {
    if (hasKey(formatDefinitions, countryCode) && hasKey(formatDefinitions[countryCode], 'name')) {
        return formatDefinitions[countryCode]['name'];
    }
    return countryCode;
};

const getFieldLabel = (countryCode, field) => {
    let type;
    if (field === 'postalCode') {
        type = 'zip_name_type';
    }
    else if (field === 'administrativeArea') {
        type = 'state_name_type';
    }
    if (!type) {
        return {
            key: field,
            label: field
        };
    }
    if (hasKey(formatDefinitions, countryCode) && hasKey(formatDefinitions[countryCode], type)) {
        return {
            key: field,
            label: formatDefinitions[countryCode][type],
            zip: formatDefinitions[countryCode]['zip']
        };
    }
    return {
        key: field,
        label: field
    };
};

export const formatAddress = (address) => {
    address = pruneAddress(address);
    const format = getFormat(address.postalCountry || 'ZZ');
    const formatParts = getFormatParts(format);
    const prunedFormat = pruneFormat(formatParts, address);

    const lines = [];
    const fields = [];
    let currentLine = '';

    for (const part of prunedFormat) {
        if (part === '%n') {
            if (currentLine.length > 0) {
                lines.push(currentLine);
                currentLine = '';
            }
            continue;
        }

        const field = getField(part);

        if (field) {
            if (field === 'addressLines') {
                const addressLines = address.addressLines;

                if (addressLines.length > 0) {
                    currentLine += addressLines[0];

                    if (addressLines.length > 1) {
                        lines.push(currentLine);
                        fields.push(field);
                        currentLine = '';
                        lines.push(...addressLines.splice(1));
                    }
                }
            } else {
                fields.push(field);
                currentLine += address[field];
            }

            continue;
        }

        currentLine += part;
    }

    if (currentLine.length > 0) {
        lines.push(currentLine);
    }

    const countryName = getCountryName(address.postalCountry);
    if (countryName) {
        lines.push(countryName);
    }

    return {
        fields: fields,
        address: lines
    };
};

const getAddress = (address, fields) => {
    let addressLines = [];
    if (!address || !address.country) {
        return [];
    }
    if (address.line) {
        addressLines.push(address.line);
    }
    if (address.lineTwo) {
        addressLines.push(address.lineTwo);
    }
    const _address = {
        postalCountry: address.country.toUpperCase(),
        administrativeArea : address.state,
        locality: address.city,
        postalCode: address.zip,
        organization: '',
        name: '',
        addressLines : addressLines,
    };
    return fields? formatAddress(_address).fields : formatAddress(_address).address;
}

export const getAddressFormat = (address) => {
    return getAddress(address, false);
};

export const getAddressFormatFields = (country) => {
    const address = {
        country: country,
        state: '.',
        city: '.',
        zip: '.',
        line: '.',
        lineTwo: '.'
    };
    const fields = getAddress(address, true);
    let data = [];
    for (let i = 0; i < fields.length; i++) {
        const field = fields[i];
        data.push(getFieldLabel(country, field));
    }
    return data;
};

export const getCountries = () => {
    return formatDefinitions;
};